/**
 * A TallyPrime connection, once it is the receivables source: its state, how
 * current its figures are, what it is connected to, and the owner's controls
 * (docs/tally/09). Feature branch only.
 *
 * "Sync now" is honest about what it does: the connector reads TallyPrime at
 * its next check-in, within five minutes — Timely cannot reach into the
 * customer's computer, by design. The credential is never shown; renewing it
 * asks the connector to switch to a new one at its next check-in.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Card, ConfirmDialog, Disclosure, Panel } from '@timely/ui';
import { useFormatters } from '../../book/useFormatters';
import { FormNotice } from '../../screens/AuthLayout';
import {
    approveSettlement, renameConnector, requestRotation, requestSync, revokeConnector, type ConnectorStatus,
} from '../useConnector';
import { NETWORK_EXPOSED, SECURITY_NOTE, STATE, TROUBLESHOOTING, ago, connectionState, freshnessLine } from './tallyCopy';

/** The time, re-read every half minute, so "3 minutes ago" stays true. */
export function useNow(everyMs = 30_000): Date {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), everyMs);
        return () => clearInterval(timer);
    }, [everyMs]);
    return now;
}

export function TroubleshootingAndSecurity({ status }: { status: ConnectorStatus | null }) {
    const exposed = status?.lastReport?.tallyListensOnNetwork === true;
    return (
        <Disclosure className="mt-5" title="Troubleshooting and security" summary={exposed ? 'TallyPrime is open to your network — see Security' : 'What to do if something is wrong'}>
            {exposed && (
                <div role="note" className="mb-4 rounded-control bg-warn-bg px-3.5 py-3 text-body text-label-2">
                    <span className="font-semibold text-warn">Security: </span>{NETWORK_EXPOSED}
                </div>
            )}
            <dl className="space-y-3">
                {TROUBLESHOOTING.map(t => (
                    <div key={t.id} id={`trouble-${t.id}`}>
                        <dt className="text-body font-semibold text-label">{t.title}</dt>
                        <dd className="text-secondary text-label-2 mt-0.5">{t.body}</dd>
                    </div>
                ))}
                <div>
                    <dt className="text-body font-semibold text-label">Security</dt>
                    <dd className="text-secondary text-label-2 mt-0.5">{SECURITY_NOTE}</dd>
                </div>
            </dl>
        </Disclosure>
    );
}

export function ConnectionPanel({
    workspaceId, status, canManage, canSync, onChanged, onReplaced,
}: {
    workspaceId: string;
    status: ConnectorStatus;
    canManage: boolean;
    canSync: boolean;
    onChanged: () => Promise<void> | void;
    /** After "Replace connector": start pairing a new one. */
    onReplaced: () => void;
}) {
    const { date, money } = useFormatters();
    const now = useNow();
    const state = connectionState(status, now);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [renaming, setRenaming] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<'disconnect' | 'replace' | null>(null);

    const run = async (key: string, work: () => Promise<string | null>) => {
        if (busy) return;
        setBusy(key);
        setNotice(null);
        try { const text = await work(); if (text) setNotice({ tone: 'ok', text }); await onChanged(); } catch (e) {
            setNotice({ tone: 'error', text: e instanceof Error ? e.message : 'That could not be done just now.' });
        } finally { setBusy(null); }
    };

    // As the freshness on every screen says it (docs/product/phase-0/01): TallyPrime or the company closed is said as soon as the
    // connector reports it, not only once the figures are six hours old.
    const report = status.status === 'active' ? status.lastReport : null;
    const problem = state === 'needs_attention' ? status.lastError
        : state === 'offline' ? TROUBLESHOOTING.find(t => t.id === 'offline')!.body
            : report?.tallyReachable === false ? TROUBLESHOOTING.find(t => t.id === 'not-running')!.body
                : report?.companyLoaded === false && status.lastSuccessAt ? TROUBLESHOOTING.find(t => t.id === 'no-company')!.body
                    : state === 'stale' ? (report?.lastError ?? 'Timely Connector is checking in, but no update has gone through. Open TallyPrime and its company on that computer.')
                        : null;
    const held = status.heldSettlement;

    return (
        <>
            {notice && <FormNotice tone={notice.tone}>{notice.text}</FormNotice>}
            <Panel
                id="tally-connection"
                title={<span className="flex items-center gap-3">TallyPrime <Badge variant="text" dot tone={STATE[state].tone}>{STATE[state].label}</Badge></span>}
                description={freshnessLine(state, status, now, date)}
                actions={canSync && status.status === 'active' ? (
                    <Button variant="secondary" busy={busy === 'sync'} disabled={busy !== null}
                        onClick={() => void run('sync', async () => { await requestSync(workspaceId); return 'Asked. Timely Connector will read TallyPrime at its next check-in, within 5 minutes.'; })}>
                        Sync now
                    </Button>
                ) : undefined}
                bodyClassName="px-5 py-4"
            >
                {problem && (
                    <div role="alert" className="mb-4 rounded-control bg-dang-bg px-3.5 py-3 text-body text-dang">{problem}</div>
                )}
                {status.syncRequestedAt && status.status === 'active' && (
                    <p className="mb-3 text-secondary text-label-3">A sync was asked for {ago(status.syncRequestedAt, now, date)}; it happens at the connector’s next check-in.</p>
                )}
                <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2 text-body">
                    <div><dt className="text-caption text-label-3">TallyPrime company</dt><dd className="font-semibold text-label break-words">{status.sourceCompany?.name ?? '—'}</dd>
                        {status.sourceCompany && <dd className="font-mono text-caption text-label-3 break-all">{status.sourceCompany.id}</dd>}</div>
                    <div>
                        <dt className="text-caption text-label-3">Computer</dt>
                        {renaming === null ? (
                            <dd className="text-label break-words">{status.deviceName}
                                {canManage && <button type="button" className="ml-2 text-secondary text-accent-text font-semibold hover:underline" onClick={() => setRenaming(status.deviceName)}>Rename</button>}
                            </dd>
                        ) : (
                            <dd>
                                <form className="flex flex-wrap items-center gap-2" onSubmit={e => { e.preventDefault(); void run('rename', async () => { await renameConnector(workspaceId, status.id, renaming); setRenaming(null); return 'Renamed.'; }); }}>
                                    <input aria-label="Computer name" value={renaming} maxLength={80} onChange={e => setRenaming(e.target.value)}
                                        className="min-h-9 rounded-control border border-separator bg-card px-2 text-body" />
                                    <Button variant="primary" busy={busy === 'rename'} type="submit">Save</Button>
                                    <Button variant="secondary" type="button" onClick={() => setRenaming(null)}>Cancel</Button>
                                </form>
                            </dd>
                        )}
                    </div>
                    <div><dt className="text-caption text-label-3">Last check-in</dt><dd className="text-label-2">{ago(status.lastSeenAt, now, date)}</dd></div>
                    <div><dt className="text-caption text-label-3">Last successful update</dt><dd className="text-label-2">{ago(status.lastSuccessAt, now, date)}</dd></div>
                    <div><dt className="text-caption text-label-3">Last full reconciliation</dt><dd className="text-label-2">{ago(status.lastAppliedAt, now, date)} <span className="text-caption text-label-3">(at least every 6 hours)</span></dd></div>
                    <div><dt className="text-caption text-label-3">Timely Connector</dt><dd className="text-label-2">{status.connectorVersion ? `Version ${status.connectorVersion}` : '—'}</dd></div>
                    <div><dt className="text-caption text-label-3">Credential</dt><dd className="text-label-2">Issued {ago(status.credentialIssuedAt, now, date)}; renewed automatically every 30 days{status.rotateRequestedAt ? ' · renewal asked for' : ''}</dd></div>
                    {status.replacedSheet && <div><dt className="text-caption text-label-3">Google Sheet</dt><dd className="text-label-2">Replaced by TallyPrime; its settings are kept.</dd></div>}
                </dl>
                {/* Managing the connection belongs to the connection: one row under its facts, not a second card with
                    one heading and three buttons (2026-10 global sweep). */}
                {canManage && (
                    <section aria-labelledby="tally-manage" className="mt-5 pt-4 border-t border-separator flex flex-wrap items-center gap-x-3 gap-y-2">
                        <h3 id="tally-manage" className="text-secondary font-semibold text-label-3 mr-auto">Manage the connection</h3>
                        <Button variant="secondary" size="sm" busy={busy === 'rotate'} disabled={busy !== null}
                            onClick={() => void run('rotate', async () => { await requestRotation(workspaceId); return 'Asked. Timely Connector switches to a new credential at its next check-in; the old one stops working ten minutes later.'; })}>
                            Renew credential
                        </Button>
                        <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => setConfirm('replace')}>Replace connector</Button>
                        <Button variant="danger" size="sm" disabled={busy !== null} onClick={() => setConfirm('disconnect')}>Disconnect</Button>
                    </section>
                )}
            </Panel>

            {held && (
                <Card className="mt-5 p-5">
                    <h3 className="text-heading font-semibold text-label">An update is waiting for your review</h3>
                    <p className="mt-1 text-body text-label-2">
                        The latest read from TallyPrime would mark {held.settle} of {held.open} accounts as paid at once. Nothing has been changed.
                        If these customers really have paid, approve it; if not, check that the right company is open in TallyPrime.
                    </p>
                    {held.accounts && held.accounts.length > 0 && (
                        <ul className="mt-3 grid gap-1 sm:grid-cols-2 text-secondary text-label-2">
                            {held.accounts.map(a => <li key={a.company}>{a.company} <span className="num text-label-3">{money.exact(a.amount)}</span></li>)}
                        </ul>
                    )}
                    {canManage && (
                        <div className="mt-4">
                            <Button variant="primary" busy={busy === 'approve'} disabled={busy !== null || !status.heldSettlementRead}
                                onClick={() => void run('approve', async () => { await approveSettlement(workspaceId, status.id, status.heldSettlementRead!); return 'Approved. It applies at the connector’s next check-in.'; })}>
                                These customers have paid
                            </Button>
                        </div>
                    )}
                </Card>
            )}

            <TroubleshootingAndSecurity status={status} />

            <ConfirmDialog
                open={confirm === 'disconnect'}
                title="Disconnect TallyPrime?"
                confirmLabel="Disconnect"
                busy={busy === 'disconnect'}
                onCancel={() => setConfirm(null)}
                onConfirm={() => { setConfirm(null); void run('disconnect', async () => { await revokeConnector(workspaceId, status.id); return 'Disconnected. Your accounts keep their last figures and all their history.'; }); }}
            >
                <p>Timely Connector on “{status.deviceName}” stops working at once, and no more updates come from TallyPrime.</p>
                <p className="mt-2">Your customers keep their last figures from TallyPrime, and every follow-up, note, promise, cheque and reminder stays exactly as it is. Nothing is deleted, and no other source is switched on.</p>
            </ConfirmDialog>
            <ConfirmDialog
                open={confirm === 'replace'}
                title="Replace this connector?"
                confirmLabel="Disconnect and pair a new one"
                busy={busy === 'replace'}
                onCancel={() => setConfirm(null)}
                onConfirm={() => { setConfirm(null); void run('replace', async () => { await revokeConnector(workspaceId, status.id); onReplaced(); return null; }); }}
            >
                <p>The connector on “{status.deviceName}” is disconnected, and you pair the new one with a fresh code. Accounts from the same TallyPrime company are recognised automatically; your history is kept.</p>
            </ConfirmDialog>
        </>
    );
}
